import type { OoxmlTableMeta } from "../ooxml/tableCore";
import { DEFAULT_PAGE_WIDTH_PX } from "../units";

function resolveTableReferenceWidthPx(meta: OoxmlTableMeta): number {
  if (meta.widthPx != null && meta.widthPx > 0) return meta.widthPx;
  if (meta.widthPercent != null && meta.widthPercent > 0) {
    return Math.round((meta.widthPercent / 100) * DEFAULT_PAGE_WIDTH_PX);
  }
  return DEFAULT_PAGE_WIDTH_PX;
}

/** Resolve logical column widths (px) from gridCol or per-cell tcW. */
export function resolveColumnWidthsPx(meta: OoxmlTableMeta): number[] {
  if (meta.gridColWidthsPx?.length) {
    return meta.gridColWidthsPx.filter((w) => w > 0);
  }

  const colCount = meta.rows.reduce(
    (max, row) =>
      Math.max(max, row.cells.reduce((sum, cell) => sum + cell.colSpan, 0)),
    0,
  );
  if (!colCount) return [];

  const refWidth = resolveTableReferenceWidthPx(meta);
  const widths: Array<number | undefined> = Array(colCount).fill(undefined);

  for (const row of meta.rows) {
    let col = 0;
    for (const cell of row.cells) {
      if (cell.widthPx != null && cell.widthPx > 0) {
        if (cell.colSpan === 1) {
          widths[col] = cell.widthPx;
        } else {
          const perCol = Math.round(cell.widthPx / cell.colSpan);
          for (let i = 0; i < cell.colSpan; i++) {
            widths[col + i] ??= perCol;
          }
        }
      } else if (cell.widthPercent != null && cell.widthPercent > 0) {
        const cellWidthPx = Math.round((cell.widthPercent / 100) * refWidth);
        if (cell.colSpan === 1) {
          widths[col] = cellWidthPx;
        } else {
          const perCol = Math.round(cellWidthPx / cell.colSpan);
          for (let i = 0; i < cell.colSpan; i++) {
            widths[col + i] ??= perCol;
          }
        }
      }
      col += cell.colSpan;
    }
  }

  if (!widths.some((w) => w != null && w > 0)) return [];
  return widths.map((w) => w ?? 0);
}

function gridColumnIndex(cells: HTMLTableCellElement[], target: HTMLTableCellElement): number {
  let col = 0;
  for (const cell of cells) {
    if (cell === target) return col;
    col += cell.colSpan || 1;
  }
  return -1;
}

function applyColwidthToFirstRow(
  table: HTMLTableElement,
  columnWidths: number[],
): void {
  const firstRow = table.querySelector("tr");
  if (!firstRow) return;

  const cells = Array.from(firstRow.children).filter(
    (node): node is HTMLTableCellElement => node instanceof HTMLTableCellElement,
  );

  for (const cell of cells) {
    const colIndex = gridColumnIndex(cells, cell);
    if (colIndex < 0) continue;

    const colspan = cell.colSpan || 1;
    const slice = columnWidths.slice(colIndex, colIndex + colspan);
    if (slice.length !== colspan || !slice.every((w) => w > 0)) continue;

    cell.setAttribute("colwidth", slice.join(","));
  }
}

function applyColGroup(table: HTMLTableElement, columnWidths: number[]): number {
  table.querySelector("colgroup")?.remove();

  const colgroup = table.ownerDocument.createElement("colgroup");
  let applied = 0;

  for (const width of columnWidths) {
    if (width <= 0) continue;
    const col = table.ownerDocument.createElement("col");
    col.style.width = `${width}px`;
    colgroup.appendChild(col);
    applied++;
  }

  if (applied > 0) {
    table.insertBefore(colgroup, table.firstChild);
  }

  return applied;
}

/**
 * Apply Word-like column geometry for TipTap (colgroup + colwidth attrs).
 */
export function applyTableGeometry(
  table: HTMLTableElement,
  meta: OoxmlTableMeta,
): { columnWidthsApplied: number; tableLayoutApplied: boolean } {
  const columnWidths = resolveColumnWidthsPx(meta);
  const useFixedLayout =
    meta.layoutMode === "fixed" ||
    (meta.layoutMode !== "autofit" && columnWidths.length > 0);

  let tableLayoutApplied = false;
  if (useFixedLayout && !table.style.tableLayout) {
    table.style.tableLayout = "fixed";
    tableLayoutApplied = true;
  }

  if (columnWidths.length === 0) {
    return { columnWidthsApplied: 0, tableLayoutApplied };
  }

  const columnWidthsApplied = applyColGroup(table, columnWidths);
  applyColwidthToFirstRow(table, columnWidths);

  return { columnWidthsApplied, tableLayoutApplied };
}
